import React, {useContext, useEffect, useState} from "react";
import SessionManager from "@Core/session";
import {getSystemId, hasCapability} from "@Utils";
import ModuleUser from "@Components/groups/ModuleUser";
import ModuleAddress from "@Components/groups/ModuleAddress";
import ModuleCredit from "@Components/groups/ModuleCredit";
import ModuleGroup from "@Components/groups/ModuleGroup";
import ModuleCreateUser from "@Components/groups/ModuleCreateUser";
import ModuleAddGroupUsers from "@Components/groups/ModuleAddGroupUsers";
import {GroupContext, SystemContext, UserContext} from "@Constants";
import {MainContext} from "@Context/provider";
import "@Styles/pages/groupsPage.scss";
import ModuleUserActions from "@Components/groups/ModuleUserActions";
import ModuleLicense from "@Components/groups/ModuleLicense";
import ModuleUserGroups from "@Components/groups/ModuleUserGroups";

interface ICurrent {
    context: number;
    instance: string;
}

const GroupsInspector: React.FC = () => {
    const {ui: uiState} = useContext(MainContext);
    const [current, setCurrent] = useState<ICurrent>({
        context: SystemContext,
        instance: getSystemId()
    });

    /**
     * Return the currently selected context
     */
    const getCurrentContext = () => {
        if (uiState.currentGroup === null) return SystemContext;
        if (uiState.currentUser === null) return GroupContext;
        return UserContext;
    };

    /**
     * Return the currently selected instance
     */
    const getCurrentInstance = () => {
        if (uiState.currentGroup === null) return getSystemId();
        if (uiState.currentUser === null) return uiState.currentGroup.instance;
        return uiState.currentUser.instance;
    };

    // TODO: use checkCapWithContext util
    const checkCap = (capability: string, context: number, instance = "") => {
        if (instance === getSystemId() && context !== SystemContext) return false;
        return hasCapability(
            capability,
            context,
            instance
        );
    };

    const renderModules = (): JSX.Element[] => {
        const result: JSX.Element[] = [];

        if (uiState.currentUser !== null) {

            // User Context
            const userId = uiState.currentUser?.instance;
            result.push(renderModule("user"));
            result.push(renderModule("userActions"));
            result.push(renderModule("userGroups"));
            
            if (checkCap("Address/GetUser", UserContext, userId)) {
                result.push(renderModule("address"));
            }

            if (checkCap("License/Get", SystemContext, getSystemId())) {
                result.push(renderModule("license"));
            }

            if (checkCap("Credit/GetUser", UserContext, userId)) {
                result.push(renderModule("credit"));
            }
        } else if (uiState.currentGroup !== null && uiState.currentGroup.instance !== getSystemId()) {
            // Group Context
            const groupId = uiState.currentGroup?.instance;
            result.push(renderModule("group"));

            if (checkCap("UserGroup/AddMembers", GroupContext, groupId)) {
                result.push(renderModule("addGroupMembers"));
            }

            if (checkCap("Account/Create", GroupContext, groupId)) {
                result.push(renderModule("createUser"));
            }

            if (checkCap("Address/GetGroup", GroupContext, groupId)) {
                result.push(renderModule("address"));
            }

            if (checkCap("License/GetGroup", GroupContext, groupId)) {
                result.push(renderModule("license"));
            }

            if (checkCap("Credit/GetGroup", GroupContext, groupId)) {
                result.push(renderModule("credit"));
            }
        } else if ((uiState.currentGroup === null || uiState.currentGroup?.instance === getSystemId())) {

          // System Context
          if (checkCap("Account/Create", SystemContext)) {
            result.push(renderModule("createUser"));
          }
        }

        return result;
    };

    const renderModule = (module: string): JSX.Element => {
        const c = getCurrentContext();
        const i = getCurrentInstance();
        switch (module) {
            case "user":
                return <ModuleUser key={"user-i-" + i} instance={i} checkCap={checkCap}/>;
            case "userActions":
                return <ModuleUserActions key={"user-actions-i-" + i} instance={i} checkCap={checkCap}/>;
            case "userGroups":
                return <ModuleUserGroups key={"user-groups-i-" + i} instance={i} checkCap={checkCap}/>;
            case "group":
                return <ModuleGroup key={"group-i-" + i} instance={i} checkCap={checkCap}/>;
            case "addGroupMembers":
                return <ModuleAddGroupUsers key={"add-group-members-i-" + i} groupId={i}/>;
            case "createUser":
                return <ModuleCreateUser key={"create-group-c-" + c + "-i-" + i} context={c} instance={i} checkCap={checkCap}/>;
            case "role":
                return <></>;
            case "license":
                return <ModuleLicense key={"license-c-" + c + "-i-" + i} context={c} instance={i} checkCap={checkCap}/>;
            case "address":
                return <ModuleAddress key={"address-c-" + c + "-i-" + i} context={c} instance={i} checkCap={checkCap}/>;
            case "credit":
                return <ModuleCredit key={"credit-c-" + c + "-i-" + i} context={c} instance={i} checkCap={checkCap}/>;
        }
        return null;
    };

    useEffect(() => {
        setCurrent({
            context: getCurrentContext(),
            instance: getCurrentInstance()
        });
    }, [uiState.currentGroup, uiState.currentUser]);

    return (
        <div id="groups-inspector">
            <div id="groups-inspector-wrapper">
                {renderModules()}
            </div>
        </div>
    );
};
export default GroupsInspector;
